import { createContext, useContext, useEffect, useState, useCallback, Fragment } from "react";
import { api, getToken, setToken } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { analyticsSessionId } from "@/lib/analytics";
import { identifyUser, Sentry } from "@/instrumentation";
import { clearPrivateClientState } from "@/lib/private-client-state";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Link the authenticated user to Sentry + LogRocket every time we set
  // (or clear) the user state, so replays and errors carry the user id.
  const _syncObservability = useCallback((u) => {
    try {
      if (u && (u.id || u.email)) {
        identifyUser({ id: u.id || u._id, email: u.email, name: u.name });
      } else {
        Sentry.setUser(null);
      }
    } catch { /* noop */ }
  }, []);

  const _setUser = useCallback((u) => {
    setUser(u);
    _syncObservability(u);
  }, [_syncObservability]);

  const refresh = useCallback(async () => {
    const requestToken = getToken();
    if (!requestToken) {
      _setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      if (getToken() !== requestToken) return null;
      _setUser(data);
      return data;
    } catch (error) {
      // A temporary network failure must not sign out a paid customer.
      if (getToken() === requestToken && [401, 403].includes(error?.response?.status)) {
        setToken("");
        _setUser(null);
      }
      return null;
    } finally {
      if (getToken() === requestToken || !getToken()) setLoading(false);
    }
  }, [_setUser]);

  useEffect(() => {
    let alive = true;
    clearPrivateClientState().finally(() => { if (alive) refresh(); });
    const switched = (event) => {
      if (event.key !== "crc_session_token") return;
      _setUser(null); setLoading(true);
      clearPrivateClientState().finally(() => { if (alive) refresh(); });
    };
    window.addEventListener("storage", switched);
    return () => { alive = false; window.removeEventListener("storage", switched); };
  }, [refresh, _setUser]);

  useEffect(() => {
    // Pick up subscription repairs and checkout changes without a new login.
    let lastRefresh = 0;
    const refreshVisibleAccount = () => {
      if (!getToken() || document.visibilityState !== "visible") return;
      if (Date.now() - lastRefresh < 5000) return;
      lastRefresh = Date.now();
      refresh();
    };
    window.addEventListener("focus", refreshVisibleAccount);
    document.addEventListener("visibilitychange", refreshVisibleAccount);
    return () => {
      window.removeEventListener("focus", refreshVisibleAccount);
      document.removeEventListener("visibilitychange", refreshVisibleAccount);
    };
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await clearPrivateClientState();
    setToken(data.session_token);
    _setUser(data.user);
    return data.user;
  };

  const signup = async (email, password, name, role = null, extraHeaders = null, phone = null) => {
    // Send role in the same payload so the upstream auth backend can
    // persist it atomically — eliminates the race where a follow-up
    // /auth/role call could fail/timeout and leave the user with
    // role=null (which then bounces them to /role-select on next login).
    const payload = { email, password, name };
    if (role) payload.role = role;
    if (phone) payload.phone = phone;
    const cfg = extraHeaders ? { headers: extraHeaders } : undefined;
    const { data } = await api.post("/auth/signup", { ...payload, attribution: { ...getAttribution(), session_id: analyticsSessionId() } }, cfg);
    await clearPrivateClientState();
    setToken(data.session_token);
    _setUser(data.user);
    // Belt-and-suspenders — if the upstream ignored the role field, set
    // it now while we still have a token in localStorage.
    if (role && !data.user?.role) {
      try {
        const { data: roleData } = await api.post("/auth/role", { role });
        _setUser(roleData);
      } catch (_) { /* swallow — role-select screen catches this */ }
    }
    // Affiliate attribution — if visitor arrived via /a/{slug}, record the signup.
    try {
      const aff = sessionStorage.getItem("cr_aff") ||
                  new URLSearchParams(window.location.search).get("aff");
      if (aff) {
        await api.post("/affiliate/attribute", { slug: aff, email });
        sessionStorage.removeItem("cr_aff");
      }
    } catch (_) { /* never block signup on attribution */ }
    return data.user;
  };

  const setRole = async (role) => {
    const { data } = await api.post("/auth/role", { role });
    _setUser(data);
    return data;
  };

  const logout = async () => {
    const previousToken = getToken();
    setToken("");
    _setUser(null);
    setLoading(false);
    await clearPrivateClientState();
    try { await api.post("/auth/logout", {}, { headers: previousToken ? { Authorization: `Bearer ${previousToken}` } : {} }); } catch (_) { /* locally signed out even offline */ }
  };

  // ── Idle-timeout auto-logout ────────────────────────────────────────
  // Signs the user out after 30 minutes of no interaction. Watches all
  // meaningful signals (mouse, keyboard, touch, scroll, visibility) and
  // resets the timer on any of them. Storage-level sync means opening
  // a second tab keeps the whole session alive.
  //
  // Public pages (no user) are NOT affected — we only start the timer
  // when there's an authenticated session.
  useEffect(() => {
    if (!user) return undefined;
    const IDLE_MS = 30 * 60 * 1000;         // 30-minute timeout
    const KEY = "crc_last_activity";        // localStorage → syncs tabs
    let timerId = null;

    const stamp = () => {
      try { localStorage.setItem(KEY, String(Date.now())); } catch { /* noop */ }
      arm();
    };

    const check = async () => {
      const last = Number(localStorage.getItem(KEY) || 0);
      if (last && Date.now() - last >= IDLE_MS) {
        // Toast the user AFTER the logout completes so the message
        // isn't nuked by the login-page remount.
        await logout();
        try {
          const { toast } = await import("sonner");
          toast.info("Signed out after 30 minutes of inactivity.");
        } catch { /* sonner unavailable */ }
        return;
      }
      arm(); // still active — arm the next check
    };

    const arm = () => {
      if (timerId) clearTimeout(timerId);
      const last = Number(localStorage.getItem(KEY) || Date.now());
      const remaining = Math.max(1000, IDLE_MS - (Date.now() - last));
      timerId = setTimeout(check, remaining);
    };

    // Prime the last-activity timestamp on session start.
    stamp();
    const events = ["mousedown", "keydown", "touchstart", "scroll",
                    "visibilitychange", "focus"];
    // Passive listeners so scroll perf is untouched.
    for (const ev of events) window.addEventListener(ev, stamp, { passive: true });
    // If another tab flags activity, we accept it and reset locally.
    const onStorage = (e) => { if (e.key === KEY) arm(); };
    window.addEventListener("storage", onStorage);

    return () => {
      if (timerId) clearTimeout(timerId);
      for (const ev of events) window.removeEventListener(ev, stamp);
      window.removeEventListener("storage", onStorage);
    };
  }, [user, logout]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, setRole, refresh, setUser: _setUser }}>
      <Fragment key={`${user?.id || user?.user_id || "signed-out"}:${user?.role || ""}`}>{children}</Fragment>
    </AuthCtx.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
